import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

import { CSIS_HEADERS } from '@/constants/constants';
import { ERRORS_TO_IGNORE } from '@/constants/errorsToIgnore';
import { FULL_CREDIT_PROFILE_URL, MSE_API_BASE_URL } from '@/constants/urls';
import router from '@/router';
import { useStore } from '@/stores';
import { LOG_PREFIX } from '@/types/enums';
import { getOrGenerateId } from '@/utils/correlationId';
import { getAuthToken, getCookie, getGlobalAuthPath, getRequestError } from '@/utils/helperFunctions';
interface ApiRequestParams extends AxiosRequestConfig {
  method: 'get' | 'post' | 'put' | 'patch' | 'delete'
  url: string
  pathParams?: Record<string, string | number>
  logPrefix?: string
  skipLogging?: boolean
}

const isErrorToIgnore = ({ url, status, method }: { url: string; status?: number; method?: string }): boolean => {
  return ERRORS_TO_IGNORE.some((err) => url.includes(err.url) && status === err.status && method === err.method);
};

class Api {
  private readonly http: AxiosInstance;

  constructor() {
    this.http = axios.create({
      baseURL: MSE_API_BASE_URL,
    });
  }

  initAxiosInterceptors() {
    this.http.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        const store = useStore();
        if (error?.response?.status === 503) store.isShowErrorScreen = true;
        else if (error?.response?.status === 500) store.isShowInternalServerErrorPopup = true;
        return Promise.reject(error);
      },
    );
  }

  private getLogContext(options: ApiRequestParams) {
    const { logPrefix, method } = options;
    return {
      prefix: logPrefix || 'API',
      methodStr: method.toUpperCase(),
    };
  }

  private logError(error: unknown, options: ApiRequestParams): void {
    const { url } = options;
    const { prefix, methodStr } = this.getLogContext(options);
    const { errorData, logMessage } = getRequestError({ url, error });
    const message = `[${prefix}]: ${methodStr} ${logMessage}`;
    const logStatus = errorData?.status && isErrorToIgnore({ url, status: errorData.status, method: options.method }) ? 'warn'  : 'error';

    window.$log[logStatus](message, { error: errorData });
  }

  private logSuccess(options: ApiRequestParams): void {
    const { prefix, methodStr } = this.getLogContext(options);
    const message = `[${prefix}]: ${methodStr} request successful`;
    window.$log.info(message);
  }

  async request<T>(options: ApiRequestParams): Promise<AxiosResponse<T>> {
    const { pathParams = {}, headers = {}, skipLogging = false, logPrefix, ...opts } = options;

    try {
      const correlationId = getOrGenerateId();
      if (getCookie('CC-TOKEN')) {
        headers['X-Requested-With'] = 'XMLHttpRequest';
        headers['X-XSRF-TOKEN'] = getCookie('CC-TOKEN') as string;
      }

      headers['Is-Old-Design'] ??= false;
      headers['X-Correlation-Id'] = correlationId;

      if (pathParams) {
        Object.keys(pathParams).forEach((param) => {
          opts.url = opts.url.replace(param, pathParams[param] as string);
        });
      }

      const response: AxiosResponse = await this.http({
        withCredentials: true,
        headers: {
          authorization: await getAuthToken().then(token => token ? `Bearer ${token}` : ''),
          'Source-Code': 'MSE',
          ...CSIS_HEADERS,
          ...headers,
        },
        ...opts,
      });

      if (!skipLogging) {
        this.logSuccess(options);
      }

      return response;
    } catch (error) {
      if (!skipLogging) {
        this.logError(error, options);
      }
      
      const status = (error as any)?.response?.status;
      const isFullReport = options.url === FULL_CREDIT_PROFILE_URL;
      if ((status === 401 || status === 403)) {
        if (isFullReport) {
          window.$log.info(`[${LOG_PREFIX.UNAUTHORISED}]: Redirecting to re-authentication`);
          router.replace({ name: 'Reauthenticate' });
        } else {
          window.$log.info(`[${LOG_PREFIX.UNAUTHORISED}]: ${options.method.toUpperCase()} ${options.url} request failed with status ${status}. Redirecting to global auth`);
          if (import.meta.env.MODE !== 'development' && !import.meta.env.VITE_PROXY_ENV) {
            window.location.replace(getGlobalAuthPath());
          }
        }
      }
      
      throw error;  
    }
  }
}

const api = new Api();

api.initAxiosInterceptors();

export default api.request.bind(api);
